import React, { useRef } from 'react';
import { Box, Button, Chip, FormHelperText, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import colors from '../../../config/colors';

// ─── Types & constants ───────────────────────────────────────────────────────

export type DocKey =
  | 'copiaCedulaTutor'
  | 'guardaCustodia'
  | 'actaNacimiento'
  | 'diagnosticoMedico'
  | 'certificadoConadis'
  | 'pruebasPsicometricas'
  | 'documentacionApoyo';

export interface DocumentFile {
  file: File | null;
  error: string;
}

export const SIZE_LIMITS_MB: Record<DocKey, number> = {
  copiaCedulaTutor: 3,
  guardaCustodia: 5,
  actaNacimiento: 5,
  diagnosticoMedico: 5,
  certificadoConadis: 5,
  pruebasPsicometricas: 10,
  documentacionApoyo: 5,
};

export const ACCEPTED_FORMATS = '.pdf,.jpg,.jpeg,.png';
const ACCEPTED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

export const INFO_DIAGNOSTICO =
  'Este documento debe estar debidamente timbrado, firmado y sellado, con exequátur del especialista, y no debe presentar tachaduras, borrones ni irregularidades.';

export const INITIAL_DOCS: Record<DocKey, DocumentFile> = {
  copiaCedulaTutor: { file: null, error: '' },
  guardaCustodia: { file: null, error: '' },
  actaNacimiento: { file: null, error: '' },
  diagnosticoMedico: { file: null, error: '' },
  certificadoConadis: { file: null, error: '' },
  pruebasPsicometricas: { file: null, error: '' },
  documentacionApoyo: { file: null, error: '' },
};

// ─── Component ───────────────────────────────────────────────────────────────

interface DocumentUploadItemProps {
  label: string;
  required: boolean;
  docKey: DocKey;
  docs: Record<DocKey, DocumentFile>;
  onFileChange: (key: DocKey, file: File | null, error: string) => void;
  infoText?: string;
}

export const DocumentUploadItem: React.FC<DocumentUploadItemProps> = ({
  label,
  required,
  docKey,
  docs,
  onFileChange,
  infoText,
}) => {
  const doc = docs[docKey];
  const maxSizeMB = SIZE_LIMITS_MB[docKey];
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      onFileChange(docKey, null, '');
      return;
    }
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      onFileChange(docKey, null, `El archivo ${file.name} tiene un formato no permitido. Solo se aceptan PDF, JPG, JPEG y PNG.`);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      onFileChange(
        docKey,
        null,
        `El archivo ${file.name} supera el tamaño permitido. Por favor, carga un archivo menor a ${maxSizeMB}MB`
      );
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    onFileChange(docKey, file, '');
  };

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        border: `1px solid ${doc.error ? '#d32f2f' : colors.grayLight}`,
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant='body2' sx={{ fontFamily: 'Montserrat', fontWeight: 'bold' }}>
          {label}
        </Typography>
        <Chip
          label={required ? 'Requerido' : 'Opcional'}
          size='small'
          color={required ? 'primary' : 'default'}
          variant='outlined'
        />
        {infoText && (
          <Tooltip title={infoText} arrow>
            <IconButton size='small' sx={{ p: 0.25 }}>
              <InfoOutlinedIcon fontSize='small' color='info' />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button
          component='label'
          variant='outlined'
          size='small'
          sx={{ textTransform: 'none', fontFamily: 'Montserrat' }}
        >
          Seleccionar archivo
          <input ref={inputRef} type='file' hidden accept={ACCEPTED_FORMATS} onChange={handleFileChange} />
        </Button>
        {doc.file && (
          <Typography variant='caption' sx={{ fontFamily: 'Montserrat', color: colors.gray }}>
            {doc.file.name}
          </Typography>
        )}
        {(doc.file || doc.error) && (
          <Tooltip title='Eliminar archivo'>
            <IconButton
              size='small'
              onClick={() => {
                onFileChange(docKey, null, '');
                if (inputRef.current) inputRef.current.value = '';
              }}
            >
              <ClearIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {doc.error ? (
        <FormHelperText error sx={{ mt: 0.5 }}>
          {doc.error}
        </FormHelperText>
      ) : (
        <Typography
          variant='caption'
          sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontFamily: 'Montserrat' }}
        >
          Formatos permitidos: PDF, JPG, PNG · Máx. {maxSizeMB}MB
        </Typography>
      )}
    </Box>
  );
};
